import React, { useState, useMemo } from 'react';

interface ChartDataPoint {
  date: string;
  count: number;
}

interface LogVolumeSVGChartProps {
  data: ChartDataPoint[];
  onPointClick?: (dataPoint: ChartDataPoint) => void;
}

// Helper function to calculate dot radius based on value
function calculateDotRadius(value: number, maxValue: number, minRadius = 2, maxRadius = 6): number {
  if (maxValue === 0) return minRadius;
  const normalizedValue = value / maxValue;
  return minRadius + (normalizedValue * (maxRadius - minRadius));
}

export function LogVolumeSVGChart({ data, onPointClick }: LogVolumeSVGChartProps) {
  const [hoveredPoint, setHoveredPoint] = useState<{ 
    index: number; 
    x: number; 
    y: number; 
    svgY: number;
    rectTop: number;
    rectHeight: number;
    value: number 
  } | null>(null);
  
  const chartDimensions = {
    width: 800,
    height: 120,
    padding: { top: 20, right: 30, bottom: 30, left: 50 }
  };
  
  const chartArea = {
    width: chartDimensions.width - chartDimensions.padding.left - chartDimensions.padding.right,
    height: chartDimensions.height - chartDimensions.padding.top - chartDimensions.padding.bottom
  };

  const { points, maxValue, yAxisLabels, xAxisLabels } = useMemo(() => {
    if (!data || data.length === 0) {
      return { points: [], maxValue: 0, yAxisLabels: [], xAxisLabels: [] };
    }

    const maxValue = Math.max(...data.map(d => d.count));
    const minValue = 0; // Always start from 0 for cleaner visualization
    
    // Calculate Y-axis labels (0, max/2, max)
    const yAxisLabels = [
      { value: maxValue, y: Math.round(chartDimensions.padding.top) },
      { value: Math.round(maxValue / 2), y: Math.round(chartDimensions.padding.top + chartArea.height / 2) },
      { value: 0, y: Math.round(chartDimensions.padding.top + chartArea.height) }
    ];

    // Calculate X-axis labels (show all dates since there are fewer points)
    const xAxisLabels = data.map((point, index) => {
      const x = Math.round(chartDimensions.padding.left + (index / (data.length - 1)) * chartArea.width);
      return { 
        label: point.date, 
        x 
      };
    });

    // Calculate points with proper spacing and only for non-zero values
    const points = data
      .map((point, index) => {
        if (point.count === 0) return null; // Skip zero values
        
        const x = Math.round(chartDimensions.padding.left + (index / (data.length - 1)) * chartArea.width);
        const y = Math.round(chartDimensions.padding.top + chartArea.height - ((point.count - minValue) / maxValue) * chartArea.height);
        
        return { 
          x, 
          y, 
          data: point, 
          index,
          radius: calculateDotRadius(point.count, maxValue, 3, 7) // Slightly larger dots
        };
      })
      .filter(Boolean); // Remove null values

    return { points, maxValue, yAxisLabels, xAxisLabels };
  }, [data, chartArea, chartDimensions]);

  const handleMouseMove = (event: React.MouseEvent<SVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const scaleX = chartDimensions.width / rect.width;
    const adjustedMouseX = mouseX * scaleX;

    let closestPoint = null;
    let minDistance = Infinity;

    points.forEach((point) => {
      const distance = Math.abs(point.x - adjustedMouseX);
      if (distance < minDistance) {
        minDistance = distance;
        closestPoint = point;
      }
    });

    if (closestPoint && minDistance < 50) {
      setHoveredPoint({
        index: closestPoint.index,
        x: event.clientX,
        y: closestPoint.y,
        svgY: closestPoint.y,
        rectTop: rect.top,
        rectHeight: rect.height,
        value: closestPoint.data.count
      });
    } else {
      setHoveredPoint(null);
    }
  };

  const handleMouseLeave = () => {
    setHoveredPoint(null);
  };

  const handlePointClick = (point: any) => {
    if (onPointClick) {
      onPointClick(point.data);
    }
  };

  if (!data || data.length === 0) {
    return (
      <div className="h-30 w-full flex items-center justify-center text-muted-foreground text-sm">
        No data available
      </div>
    );
  }

  return (
    <div className="relative h-30 w-full">
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${chartDimensions.width} ${chartDimensions.height}`}
        className="overflow-visible cursor-pointer"
        shapeRendering="crispEdges"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >        
        {/* Grid lines aligned with Y-axis labels */}
        <g className="opacity-20">
          {yAxisLabels.map((label) => (
            <line
              key={label.value}
              x1={chartDimensions.padding.left}
              y1={label.y}
              x2={chartDimensions.width - chartDimensions.padding.right}
              y2={label.y}
              stroke="hsl(var(--foreground))"
              strokeWidth="1"
            />
          ))}
          
          {/* Subtle vertical grid lines */}
          {xAxisLabels.map((label) => (
            <line
              key={label.x}
              x1={label.x}
              y1={chartDimensions.padding.top}
              x2={label.x}
              y2={chartDimensions.padding.top + chartArea.height}
              stroke="hsl(var(--foreground))"
              strokeWidth="1"
              className="opacity-50"
            />
          ))}
        </g>
        
        {/* Y-axis labels */}
        {yAxisLabels.map((label) => (
          <text
            key={label.value}
            x={chartDimensions.padding.left - 10}
            y={label.y}
            textAnchor="end"
            dominantBaseline="middle"
            className="text-[10px] fill-muted-foreground"
          >
            {label.value}
          </text>
        ))}
        
        {/* X-axis labels */}
        {xAxisLabels.map((label) => (
          <text
            key={label.x}
            x={label.x}
            y={chartDimensions.height - 10}
            textAnchor="middle"
            dominantBaseline="middle"
            className="text-[10px] fill-muted-foreground"
          >
            {label.label}
          </text>
        ))}
        
        {/* Data dots (only for non-zero values) */}
        {points.map((point) => {
          const isHovered = hoveredPoint?.index === point.index;
          
          return (
            <circle
              key={point.index}
              cx={point.x}
              cy={point.y}
              r={point.radius}
              fill={isHovered ? "hsl(var(--primary-foreground))" : "hsl(var(--primary))"}
              className="cursor-pointer"
              onClick={() => handlePointClick(point)}
            />
          );
        })}
      </svg>

      {hoveredPoint && (
        <div
          className="fixed z-[100] rounded-md bg-background border px-2 py-1 shadow-lg pointer-events-none text-xs whitespace-nowrap"
          style={{
            left: Math.min(Math.max(hoveredPoint.x - 40, 10), window.innerWidth - 100),
            top: hoveredPoint.rectTop + (hoveredPoint.svgY / chartDimensions.height) * hoveredPoint.rectHeight - 40,
          }}
        >
          <span className="text-muted-foreground">{data[hoveredPoint.index]?.date}</span>
          <span className="ml-2 font-semibold text-foreground">{hoveredPoint.value}</span>
        </div>
      )}
    </div>
  );
}